/* eslint-disable
  jsx-a11y/click-events-have-key-events,
  jsx-a11y/no-static-element-interactions */
/*
  Disabling these eslint rules because we're not actually doing the bad things they're intended
  to prevent. The onclick handler is only handling <a> tag clicks which are fired even when a user
  activates the link via the keyboard.
*/
import React, { Suspense, useDeferredValue, useEffect } from 'react'

import classNames from 'clsx'
import i18n from 'i18n-literally'
import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import { CloudDownload } from '@mui/icons-material'
import { Alert } from '@mui/lab'
import {
  CircularProgress,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import { captureException as captureSentryException } from '@sentry/react'
import { camelize, underscore } from 'inflection'
import { getNavHelper } from 'internal-nav-helper'

import EulaDialog from '~/src/Account/eula'
import { Allowed, IRRIGATION_OR_NOT_METRC, pickAllowedProps } from '~/src/Flags'
import UnsavedHarvestingLeaveDialog from '~/src/Flow/UnsavedHarvestingLeaveDialog'
import IrrigationChangeNotification from '~/src/HarvestPhaseChange/Irrigation/Notification'
import { semverToNumber } from '~/src/Lib/Calculators'
import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import { idAndNameShape, renderableType } from '~/src/Lib/PropTypes'
import safeStorage from '~/src/Lib/safeStorage'
import useConcurrentConnect from '~/src/Lib/useConcurrentConnect'
import {
  defer,
  deferUntil,
  EMPTY_OBJECT,
  noop,
  throttle,
  useBreakpoint,
  useIsBreakpointDown,
} from '~/src/Lib/Utils'
import NotFound from '~/src/Routes/NotFound'
import { shape as meShape } from '~/src/Store/bundles/me'
import Button from '~/src/UI/Shared/Button'
import Head from '~/src/UI/Shared/Head'
import SomethingWentWrong from '~/src/UI/Shared/SomethingWentWrong'
import T from '~/src/UI/Shared/Typography'

import { PUBLIC_ROUTES } from '../Routes/constants'
import SpinnerRoute from '../Routes/SpinnerRoute'
import FourOhThree from './403'
import AppHeader from './AppHeader'
import config from './config'
import DialogRoute from './DialogRoute'
import Nav from './Nav'
import SnackBar from './SnackBar'
import styles from './styles'
import SubscriptionBanner from './SubscriptionBanner'

const logger = createLogger('App/Authenticated')
const getCombinedProps = memoizeOne((connectedProps, props) => ({
  ...connectedProps,
  ...props,
}))
const getRouteClass = memoizeOne(url => {
  if (!url || url.length < 2) return ''
  const list = url.split('/').filter(Boolean)
  const { 0: first, 1: middle, 2: last } = list
  if (!Array.isArray(list)) return ''
  let pieces = list
  if (list.length >= 2) {
    if (middle.match(/^\d+$/)) {
      pieces = last ? [first, last] : [first, 'detail']
    }
  }

  return camelize(['aroya', ...pieces.map(underscore)].join('_'), true)
})
const document = getGlobal().document
const storage = safeStorage()
const isProd = config.ENVIRONMENT === 'production'
const isDev = !isProd
const apiIsProd = config.API_URL === 'https://api.aroya.io'
const debugOn = storage.debug === 'true'
const showEula = !isProd && storage.showEula === 'true'
const LONG_SPINNER_TIMEOUT = !isProd ? 15000 : 5000
const bannerPosition = { vertical: 'top', horizontal: 'center' }

const clientVersion
  = debugOn && storage.clientVersion
    ? `${storage.clientVersion}*`
    : config.VERSION ?? ''

const Boom = () => {
  const error = new Error('BOOM!')
  error.isBoom = true
  throw error
}
const BlowUp = !isProd || debugOn ? () => <Boom /> : noop
const defaultRoute = () => <div />

const DeferredRoute = () => {
  useEffect(() => {
    logger.debug('[DeferredRoute] mounted')
    return () => logger.debug('[DeferredRoute] unmounted')
  }, [])
  return <div className="center-all">&nbsp;</div>
}

const PermissionFallback = ({
  classes,
  permissionsUnavailable,
  routeInfo,
  route,
  Spinner = SpinnerRoute,
  Four03 = FourOhThree
}) => {
  if (route.PermissionsFallback) {
    const { PermissionFallback: Fallback } = route
    return <Fallback />
  }
  if (route?.permissions && permissionsUnavailable) {
    return <Spinner />
  }

  return <Four03 routeInfo={routeInfo} className={classes.errorMessage} />
}

const onWaveHelloEnd = ({ animationName, target }) => {
  if (animationName === 'wavehello') {
    const el = target.closest('.hello')
    el.classList.remove('hello')
  }
}
const addHelloListener = () => document?.getElementById('root')?.addEventListener('animationend', onWaveHelloEnd)
const removeHelloListener = () => document?.getElementById('root')?.removeEventListener('animationend', onWaveHelloEnd)

export class AppComponent extends React.PureComponent {
  static displayName = 'App/Authenticated'

  static propTypes = {
    breakpoint: PropTypes.string.isRequired,
    currentMembership: idAndNameShape,
    dimensions: PropTypes.shape({
      height: PropTypes.objectOf(PropTypes.number),
      width: PropTypes.objectOf(PropTypes.number),
    }),
    dialog: PropTypes.shape({ isOpen: PropTypes.bool }),
    drawer: PropTypes.shape({ isOpen: PropTypes.bool }).isRequired,
    inflight: PropTypes.shape({
      read: PropTypes.number,
      write: PropTypes.number,
    }),
    isOnline: PropTypes.bool.isRequired,
    isLoggingOut: PropTypes.bool,
    auth: PropTypes.shape({
      authenticated: PropTypes.bool,
    }).isRequired,
    locale: PropTypes.string.isRequired,
    maintenanceModeEnabled: PropTypes.bool.isRequired,
    me: meShape,
    meIsExpired: PropTypes.bool.isRequired,
    meLastSuccess: PropTypes.number.isRequired,
    myPermissions: PropTypes.objectOf(PropTypes.bool).isRequired,
    route: (props, propName, component) => {
      const route = props[propName]
      return renderableType(
        ...(route && route.component
          ? [route, 'component', `${component}.route`]
          : [props, propName, component])
      )
    },
    routeInfo: PropTypes.shape({
      url: PropTypes.string,
      params: PropTypes.objectOf(PropTypes.string),
    }),
    snackbarMessages: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        message: PropTypes.node,
      })
    ),
    doUpdateDimensions: PropTypes.func.isRequired,
    doUpdateUrl: PropTypes.func.isRequired,
    doDismissSnackbarMessage: PropTypes.func.isRequired,
  }

  static defaultProps = {
    currentMembership: EMPTY_OBJECT,
    dialog: { isOpen: false },
    dimensions: { height: EMPTY_OBJECT, width: EMPTY_OBJECT },
    inflight: { read: 0, write: 0 },
    isLoggingOut: false,
    me: EMPTY_OBJECT,
    route: defaultRoute,
    routeInfo: { url: '' },
    snackbarMessages: [],
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  routeDeferred = false

  showSpinner = storage.showSpinner === 'true'

  constructor(props) {
    super(props)

    this.state = {
      banner: null,
      hasError: false,
      drawerOpen: false,
      routeKey: 1,
      spinner: null,
    }

    this.onNavigation = throttle(this.onNavigation.bind(this), 166)
  }

  componentDidMount() {
    if (!this.props.auth.authenticated && !this.publicAccess) {
      logger.debug('not authenticated, redirecting to login…', this.props)
      defer(() => {
        this.props.doUpdateUrl(PUBLIC_ROUTES.LOGIN)
      }, defer.priorities.high)
    }
    defer(this.props.doUpdateDimensions, defer.priorities.low)
    this.checkClientVersion()
    addHelloListener()
  }

  componentDidUpdate() {
    this.onNavigation()
    this.checkClientVersion()
  }

  componentDidCatch(error, info) {
    logger.error(JSON.stringify({ error: { message: error.message, stackTrace: error.stack }, info }, null, 2))
    if (config.SENTRY_DSN && !error.isBoom) {
      captureSentryException(error, { extra: info })
    }
  }

  componentWillUnmount() {
    removeHelloListener()
  }

  get publicAccess() {
    return this.props.route?.publicAccess ?? false
  }

  get havePermissions() {
    const { myPermissions } = this.props
    return myPermissions !== EMPTY_OBJECT
  }

  get routeClass() {
    const { routeInfo } = this.props
    return routeInfo ? getRouteClass(routeInfo.url) : ''
  }

  onDrawerToggle = () => this.setState(
    ({ drawerOpen }) => ({ drawerOpen: !drawerOpen }),
    () => defer(this.props.doUpdateDimensions, 500)
  )

  onNavigation() {
    const { hasError } = this.state
    const { route: prevRoute } = this.state
    const { route: currRoute, meLastSuccess } = this.props

    if (prevRoute && prevRoute !== currRoute) {
      if (hasError) this.resetError()

      // If we've just logged in, then wait for the me packet to switch to the new route
      if ((prevRoute?.publicAccess && !prevRoute.is404) && !currRoute.publicAccess) {
        logger.debug('deferring route:', { prevRoute, currRoute })
        this.routeDeferred = true
        logger.debug('ms since last me fetch:', Date.now() - meLastSuccess)
        deferUntil(() => {
          logger.debug('ms since last me fetch:', Date.now() - this.props.meLastSuccess)
          this.setState({ route: this.props.route, spinner: null })
          defer(() => {
            logger.debug('undeferring route', currRoute)
            this.routeDeferred = false
          }, defer.priorities.low)
        }, () => (Date.now() - this.props.meLastSuccess) < 1000, 100, 2000)
        defer(() => {
          if (this.routeDeferred) {
            this.setState(old => (old.spinner ? old : { spinner: true }))
          }
        }, 500)
        return
      }
    }
    if (this.routeDeferred) {
      return
    }
    if (prevRoute !== currRoute) {
      this.setState({ route: currRoute })
    }
  }

  resetError = () => {
    this.setState(({ routeKey }) => ({
      hasError: false,
      routeKey: routeKey + 1,
      blowUp: undefined,
    }))
  }

  checkClientVersion() {
    if (!this.props.me) return
    const { me: { targetVersion }, meIsExpired } = this.props
    if (!targetVersion || meIsExpired) return
    if (clientVersion.match(/^\d/) && semverToNumber(targetVersion) > semverToNumber(clientVersion)) {
      logger.warn(
        'client version is out of date! Have version',
        clientVersion,
        'but version',
        targetVersion,
        'wanted.'
      )
      this.setState({ banner: true })
    } else if (this.state.banner) {
      this.setState({ banner: null })
    }
  }

  render() {
    const { showSpinner, havePermissions, routeClass } = this
    const {
      auth: { authenticated },
      breakpoint,
      classes,
      currentMembership,
      dialog,
      drawer,
      inflight,
      isLoggingOut,
      isOnline,
      locale,
      maintenanceModeEnabled,
      me,
      route,
      routeInfo: { url: currentURL },
      doUpdateUrl,
    } = this.props
    let Route = route.component || route
    let { viewMode } = route
    logger.debug('[route]', route)
    const apiVersion = me?.version ?? null
    const showBanner = storage.showBanner || (this.state.banner && !storage.hideBanner)

    if (this.routeDeferred || showSpinner || (!me && (route?.actions || route?.features))) {
      logger.debug('showing spinner:', {
        me,
        route,
        spinner: this.state.spinner,
        routeDeferred: this.routeDeferred
      })
      Route = this.showSpinner ? SpinnerRoute : DeferredRoute
      viewMode = 'fullscreen'
    }

    if (maintenanceModeEnabled) {
      return (
        <T.H5 className={classes.maintenanceMessage}>
          {[
            i18n`${config.APP} is temporarily unavailable due to maintenance and should be available for usage shortly.`,
            i18n`We apologize for any inconvenience. Please contact us if you have any questions.`
          ].join('\n')}

        </T.H5>
      )
    }

    if (this.state.blowUp) {
      Route = BlowUp
    }

    if (route?.is404 && !authenticated) {
      viewMode = 'fullscreen'
    }

    const isFullScreen = viewMode === 'fullscreen'
    const allowedProps = pickAllowedProps(route)

    return (
      <>
        <Head>
          {route.title ? <title>{route.title}</title> : null}
          {isFullScreen ? (
            <style>
              {'html { overflow: hidden; }'}
            </style>
          ) : null}
          <html lang={locale || 'en'} className={classNames(routeClass, config.APP.toLowerCase())} />
        </Head>
        {inflight.read ? (
          <LinearProgress variant="query" classes={{ root: classes.readProgress }} />
        ) : null}
        {apiIsProd && isDev && !showBanner ? (
          <Snackbar anchorOrigin={bannerPosition} open={apiIsProd && isDev}>
            <Alert severity="error" variant="filled">
              <T.H1 align="center" component="span">
                PRODUCTION
              </T.H1>
            </Alert>
          </Snackbar>
        ) : null}
        {showBanner ? (
          <Snackbar anchorOrigin={bannerPosition} open={showBanner}>
            <Alert
              data-trackheight="versionBanner"
              id="aroya-version-update-banner"
              icon={<CloudDownload />}
              severity="info"
              variant="filled"
              action={(
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => getGlobal().location.reload(true)}
                >
                  {i18n`Update now`}
                </Button>
              )}
            >
              <T.Body color="inherit" component="span">
                {i18n`New\u00a0software\u00a0version\u00a0available!`}
              </T.Body>
            </Alert>
          </Snackbar>
        ) : null}
        <div
          data-testid="aroya-app-authenticated"
          id="wrapper"
          className={classNames({
            [classes.root]: true,
            [classes.dialogOpen]: dialog.isOpen,
            // eslint-disable-next-line babel/camelcase
            aroya__drawer__open: drawer.isOpen,
            [viewMode]: !!viewMode,
            [classes[viewMode]]: !!viewMode,
            [classes.noScroll]: Boolean(route?.noScroll),
            [classes[routeClass]]: Boolean(classes[routeClass]),
          })}
          onClick={getNavHelper(doUpdateUrl)}
        >
          <main id="content" className={classNames(classes.content, 'scroller')}>
            {this.state.hasError ? (
              <SomethingWentWrong
                classes={classes}
                message={this.state.errorMessage}
                onClick={this.resetError}
              />
            ) : (
              <Suspense fallback={<SpinnerRoute />}>
                <Allowed
                  Fallback={NotFound}
                  FallbackProps={{
                    classes,
                    permissionsUnavailable: (!me || !havePermissions),
                    route,
                    routeInfo: this.props.routeInfo
                  }}
                  PermissionFallback={PermissionFallback}
                  {...allowedProps}
                >
                  <Route
                    key={this.state.routeKey}
                    {...route.additionalProps}
                  />
                </Allowed>
              </Suspense>
            )}
          </main>
          <Allowed features="OVERDUE_BANNERS">
            <SubscriptionBanner classes={classes} />
          </Allowed>
          {isFullScreen ? null : (
            <>
              <AppHeader classes={classes} />
              <Nav
                authenticated={authenticated}
                currentURL={currentURL}
                currentMembership={currentMembership}
                drawerOpen={this.state.drawerOpen}
                drawerToggle={this.onDrawerToggle}
                doUpdateUrl={this.props.doUpdateUrl}
                className={classes.nav}
                isLoggingOut={isLoggingOut}
              />
            </>
          )}
          <div id="drawerRoot" className={classes.drawerRoot} />
          <footer className={classes.footer}>
            <small id="version" onClick={() => this.setState({ blowUp: true })}>
              {[
                clientVersion,
                apiVersion,
              ].filter(Boolean).join('\u2005|\u2005')}
            </small>
            {!isFullScreen && storage.debug ? (
              <Typography className={classes.debugInfo}>
                {[
                  `breakpoint: ${breakpoint}`,
                  `res: ${Math.round(this.props.dimensions.width.window || 0)
                  }x${Math.round(this.props.dimensions.height.window || 0)
                  } (${this.props.dimensions.width.window / this.props.dimensions.height.window > 1
                    ? 'landscape'
                    : 'portrait'
                  })`,
                  `content: ${Math.round(this.props.dimensions.width.content)
                  }x${Math.round(this.props.dimensions.height.content)
                  }`
                ].join('\u2005|\u2005')}
              </Typography>
            ) : null}
          </footer>
        </div>
        {!authenticated || isLoggingOut ? null : (
          <>
            <SnackBar
              doDismissSnackbarMessage={this.props.doDismissSnackbarMessage}
              snackbarMessages={this.props.snackbarMessages}
            />
            {inflight.write ? (
              <CircularProgress classes={{ root: classes.writeProgress }} />
            ) : null}
            <DialogRoute />
            <div id="modalRoot" className={classes.modalRoot} />
            <div id="menuRoot" className={classes.modalRoot} />
            <div id="mentionsRoot" className={classes.modalRoot}>
              {!isOnline && (
                <span className={classes.offlineIndicator}>{i18n`Offline`}</span>
              )}
            </div>
            <Allowed actions="change_irrigation" features={IRRIGATION_OR_NOT_METRC}>
              <IrrigationChangeNotification />
            </Allowed>
            <UnsavedHarvestingLeaveDialog />
          </>
        )}
      </>
    )
  }
}

const useStyles = makeStyles(styles, { name: AppComponent.displayName })
const Connected = props => {
  const connectedProps = useConcurrentConnect(
    'selectAuth',
    'selectCurrentFacility',
    'selectCurrentMembership',
    'selectDialog',
    'selectDimensions',
    'selectDrawer',
    'selectFacilities',
    'selectInflight',
    'selectIsLoggingOut',
    'selectIsOnline',
    'selectLocale',
    'selectMaintenanceModeEnabled',
    'selectMeRaw',
    'selectMyPermissions',
    'selectQueryString',
    'selectSnackbarMessages',
    'selectSystemRaw',
    'doDismissSnackbarMessage',
    'doUpdateDimensions',
    'doUpdateUrl',
  )
  const { dialogRoute, route, routeInfo } = useConnect('selectDialogRoute', 'selectRoute', 'selectRouteInfo')
  const deferredDialogRoute = useDeferredValue(dialogRoute)
  const deferredRoute = useDeferredValue(route)
  const { meRaw, systemRaw, ...combinedProps } = getCombinedProps(connectedProps, props)
  const breakpoint = useBreakpoint()
  const isSmDown = useIsBreakpointDown('sm', breakpoint)
  const classes = useStyles(combinedProps)
  const { data: me } = meRaw ?? EMPTY_OBJECT
  const eulaCurrent = me && ((!me.eulaVersion && me.eulaAccepted) || me.eulaExempt || (me.eulaVersion === me.eulaAccepted))
  const meLoaded = Boolean(!meRaw.isLoading && me)

  return showEula || (isProd && meLoaded && !eulaCurrent) ? (
    <EulaDialog />
  ) : (
    <AppComponent
      {...combinedProps}
      breakpoint={breakpoint}
      classes={classes}
      dialogRoute={deferredDialogRoute}
      isSmDown={isSmDown}
      me={me}
      meIsExpired={meRaw.isExpired}
      meIsLoading={meRaw.isLoading}
      meLastError={meRaw.errorType}
      meLastSuccess={meRaw.lastSuccess || 0}
      route={deferredRoute}
      routeInfo={routeInfo}
      systemIsLoading={systemRaw.isLoading}
      systemLastError={systemRaw.errorType}
    />
  )
}

Connected.displayName = `Connected(${AppComponent.displayName})`

export default Connected
